import { useEffect, useRef, useState } from "react";
import { captureException, captureMessage } from "@sentry/nextjs";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { analytics } from "@flare/analytics";
import { useSession } from "@flare/next-auth/react";

import { useAsyncEffect } from "@/hooks/useAsyncEffect";
import { jsonRequest } from "@/lib/network/json-request";
import { useBookitRequest } from "@/modules/v2/funnel/hooks/useBookitRequest";
import { useFunnelAnswers } from "@/modules/v2/funnel/hooks/useFunnelAnswers";
import type {
  StepExperiments,
  StepHandlers,
} from "@/modules/v2/funnel/steps/step.types";
import { BiEventsNames } from "@/services/analytics/bi-event-names";

import { CheckoutForm } from "./CheckoutForm";
import { FormSkeleton } from "./FormSkeleton";

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_KEY || "");

interface UserPayload {
  company: "Kindred" | "Marble";
  email: string;
  firstName: string;
  id: {
    marbleId?: string;
  };
  lastName: string;
  phone: string;
}

export function LssCheckout({
  experiments,
}: {
  experiments: StepExperiments;
  handlers: StepHandlers;
}) {
  const { query } = useRouter();
  const [clientSecret, setClientSecret] = useState("");
  const { funnelAnswers } = useFunnelAnswers();
  const hasStartedCreatingPaymentAccount = useRef(false);
  const { createBookitRequest } = useBookitRequest();
  const { data, status } = useSession();

  const practice = query.practice as string;
  const step = query.step as string;

  const user = data?.user;
  const marbleId = user?.marbleId;

  const isLoadingSession = status === "loading";
  const hasUserData = !!data?.user;

  const { mutateAsync: createPaymentsAccount } = useMutation<
    { clientSecret: string },
    Error,
    UserPayload
  >({
    mutationFn: async (input: UserPayload) =>
      jsonRequest(
        `${process.env.NEXT_PUBLIC_PAYMENTS_API_URL}/payments/createAccount`,
        {
          method: "POST",
          body: input,
        },
      ),
  });

  useEffect(() => {
    if (!hasUserData || isLoadingSession || marbleId) {
      return;
    }

    analytics.track(BiEventsNames.WebFunnelError, {
      current_step_key: step,
      error_type: "error_in_fetching_marble_id",
      practice,
      error_message: "Error in fetching marble id",
      auth_provider: data?.provider,
    });
    captureMessage("Error in fetching marble id", {
      extra: { step: "lss-checkout" },
    });
  }, [marbleId, isLoadingSession, hasUserData]);

  useAsyncEffect(async () => {
    if (isLoadingSession || !marbleId) return;

    try {
      if (hasStartedCreatingPaymentAccount.current) return;

      hasStartedCreatingPaymentAccount.current = true;

      const { clientSecret } = await createPaymentsAccount({
        firstName: user?.name?.first ?? "",
        lastName: user?.name?.last ?? "",
        email: user?.email ?? "",
        phone: user?.phone ?? "",
        company: "Marble",
        id: {
          marbleId,
        },
      });

      if (!clientSecret) {
        analytics.track(BiEventsNames.WebFunnelError, {
          current_step_key: step,
          error_type: "register_stripe_customer",
          practice,
        });
        throw new Error(`Failed to register Stripe customer`);
      }

      setClientSecret(clientSecret);
    } catch (error) {
      captureException(error, {
        extra: {
          action: "fetchLeadIdAndRegisterCustomer",
        },
      });
    }
  }, [isLoadingSession, marbleId, user, practice, step, createPaymentsAccount]);

  useAsyncEffect(async () => {
    if (isLoadingSession || !marbleId || funnelAnswers.bookitRequestId) return;
    await createBookitRequest(marbleId);

    // createBookitRequest should be on the dependency array but it is not memoized and causing infinite loop
  }, [isLoadingSession, marbleId, funnelAnswers.bookitRequestId]);

  const options = { clientSecret };

  // TODO - render an error UI if createPaymentsAccount failed or bookitRequestId is not found
  // TODO - redirect to signin page if the user is not logged in ?
  return !isLoadingSession &&
    marbleId &&
    clientSecret &&
    funnelAnswers.bookitRequestId ? (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm clientSecret={clientSecret} experiments={experiments} />
    </Elements>
  ) : (
    <FormSkeleton experiments={experiments} />
  );
}
